<template>
  <AuthLayout>
    <div class="signup-head">
      <div class="signup-badge">회원가입</div>
      <h1 class="signup-title">Sign up</h1>
    </div>

    <!-- STEP 1: 기본 정보 -->
    <form v-if="step === 1" class="signup-form" @submit.prevent="goTerms">
      <UiInput placeholder="이름" v-model="form.name" autocomplete="name" />

      <!-- 이메일 -->
      <div class="field-wrap">
        <UiInput
          placeholder="이메일"
          type="email"
          v-model="form.email"
          autocomplete="email"
        />
        <p v-if="form.email && !emailValid" class="form-error">
          이메일 형식이 올바르지 않습니다.
        </p>
      </div>

      <!-- 생년월일 -->
      <div class="field-wrap">
        <UiInput
          placeholder="생년월일 (YYYYMMDD)"
          v-model="form.birth"
          maxlength="8"
        />
        <p v-if="form.birth && !birthValid" class="form-error">
          생년월일 형식이 맞지 않습니다.
        </p>
      </div>

      <select class="ui-input" v-model="form.gender">
        <option value="">성별</option>
        <option value="male">남성</option>
        <option value="female">여성</option>
      </select>

      <!-- 비밀번호 -->
      <UiInput
        type="password"
        placeholder="비밀번호"
        v-model="form.password"
        autocomplete="new-password"
      />

      <!-- 비밀번호 재확인 + 에러 메시지 -->
      <div class="confirm-wrap">
        <UiInput
          type="password"
          placeholder="비밀번호 재확인"
          v-model="form.passwordConfirm"
          autocomplete="new-password"
        />
        <p v-if="passwordError" class="form-error">
          {{ passwordError }}
        </p>
      </div>

      <UiButton
        type="submit"
        :disabled="!canSubmitStep1 || loading"
        :loading="loading"
      >
        다음
      </UiButton>
    </form>

    <!-- STEP 2: 약관 동의 -->
    <div v-else-if="step === 2" class="terms-section">
      <div class="terms-header">
        <h2 class="terms-title">약관 동의</h2>
        <p class="terms-subtitle">서비스 이용을 위해 약관에 동의해주세요</p>
      </div>

      <div class="terms-list">
        <label class="term-item">
          <input type="checkbox" v-model="terms.tos" @change="syncAgreeAll" />
          <span
            ><a
              href="#"
              @click.prevent="openTermsModal('tos')"
              class="terms-link"
              >이용약관</a
            >
            에 동의합니다 <span class="required">(필수)</span></span
          >
        </label>

        <label class="term-item">
          <input
            type="checkbox"
            v-model="terms.privacy"
            @change="syncAgreeAll"
          />
          <span
            ><a
              href="#"
              @click.prevent="openTermsModal('privacy')"
              class="terms-link"
              >개인정보처리방침</a
            >
            에 동의합니다 <span class="required">(필수)</span></span
          >
        </label>

        <label class="term-item">
          <input
            type="checkbox"
            v-model="terms.sensitive_content"
            @change="syncAgreeAll"
          />
          <span
            ><a
              href="#"
              @click.prevent="openTermsModal('sensitive_content')"
              class="terms-link"
              >민감정보 처리방침</a
            >
            에 동의합니다 <span class="required">(필수)</span></span
          >
        </label>

        <label class="term-item">
          <input
            type="checkbox"
            v-model="terms.marketing"
            @change="syncAgreeAll"
          />
          <span
            ><a
              href="#"
              @click.prevent="openTermsModal('marketing')"
              class="terms-link"
              >마케팅 정보 수신</a
            >
            에 동의합니다 <span class="optional">(선택)</span></span
          >
        </label>

        <div class="terms-divider"></div>

        <!-- 전체 동의하기 -->
        <label class="term-item term-item-all">
          <input
            type="checkbox"
            v-model="terms.agreeAll"
            @change="toggleAgreeAll"
          />
          <span class="agree-all-text">전체 동의하기</span>
        </label>
      </div>

      <div class="terms-actions">
        <UiButton type="button" variant="secondary" @click="goBack">
          이전
        </UiButton>
        <UiButton
          type="button"
          :disabled="!canSubmitStep2 || loading"
          :loading="loading"
          @click="onSignup"
        >
          회원가입
        </UiButton>
      </div>
    </div>

    <!-- STEP 3: 성공 화면 -->
    <div v-else class="success-content">
      <div class="success-icon">
        <svg width="100" height="100" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="50" fill="#2A4624" />
          <circle cx="50" cy="50" r="36" fill="#78FF47" />
          <path
            d="M35 50 L45 60 L65 40"
            stroke="#000000"
            stroke-width="5"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>

      <h2 class="success-title">회원가입이 완료되었습니다!</h2>
      <p class="success-message">
        환영합니다!<br />
        이제 모든 서비스를 이용하실 수 있습니다.
      </p>

      <div class="success-actions">
        <UiButton @click="goToSignIn">로그인하기</UiButton>
      </div>
    </div>

    <!-- 약관 모달 -->
    <div v-if="showTermsModal" class="modal-overlay" @click="closeTermsModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">{{ modalTitle }}</h3>
          <button class="modal-close" @click="closeTermsModal">×</button>
        </div>
        <div class="modal-body">
          <div v-html="modalContent" class="terms-content"></div>
        </div>
        <div class="modal-footer">
          <UiButton @click="closeTermsModal">확인</UiButton>
        </div>
      </div>
    </div>
  </AuthLayout>
</template>

<script>
import { api } from "@/lib/api";
import UiButton from "@/components/UiButton.vue";
import UiInput from "@/components/UiInput.vue";
import AuthLayout from "@/components/AuthLayout.vue";

export default {
  name: "SignupView",
  components: { UiButton, UiInput, AuthLayout },
  data() {
    return {
      step: 1,
      loading: false,
      token: "",
      tokenValid: false,
      form: {
        name: "",
        email: "",
        birth: "",
        gender: "",
        password: "",
        passwordConfirm: "",
      },
      terms: {
        agreeAll: false,
        tos: false,
        privacy: false,
        sensitive_content: false,
        marketing: false,
      },
      showTermsModal: false,
      modalTitle: "",
      modalContent: "",
    };
  },
  computed: {
    emailValid() {
      const { email } = this.form;
      if (!email) return true;
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return re.test(email);
    },
    birthValid() {
      const { birth } = this.form;
      if (!birth) return true;
      if (birth.length !== 8) return false;
      const year = parseInt(birth.substring(0, 4));
      const month = parseInt(birth.substring(4, 6));
      const day = parseInt(birth.substring(6, 8));
      if (year < 1900 || year > new Date().getFullYear()) return false;
      if (month < 1 || month > 12) return false;
      if (day < 1 || day > 31) return false;
      return true;
    },
    passwordValid() {
      return this.form.password && this.form.password.length >= 6;
    },
    passwordMatch() {
      return this.form.password === this.form.passwordConfirm;
    },
    passwordError() {
      if (!this.form.passwordConfirm) return "";
      if (!this.passwordValid) return "비밀번호는 6자 이상이어야 합니다";
      if (!this.passwordMatch) return "비밀번호가 일치하지 않습니다";
      return "";
    },
    canSubmitStep1() {
      return (
        this.form.name &&
        this.emailValid &&
        this.birthValid &&
        this.form.gender &&
        this.passwordValid &&
        this.passwordMatch
      );
    },
    canSubmitStep2() {
      return (
        this.terms.tos && this.terms.privacy && this.terms.sensitive_content
      );
    },
  },
  mounted() {
    this.token = (this.$route.query.token || "").toString();
    if (!this.token) {
      this.$router.replace("/");
      return;
    }
    this.verifyToken();
  },
  methods: {
    async verifyToken() {
      try {
        const res = await api("/v1/auth/invitations/verify", {
          method: "POST",
          body: JSON.stringify({ token: this.token }),
        });
        if (!res.ok) throw new Error("invalid");
        this.tokenValid = true;
      } catch {
        this.$router.replace("/");
      }
    },
    goTerms() {
      if (!this.canSubmitStep1 || this.loading) return;
      this.step = 2;
      this.syncAgreeAll();
    },
    goBack() {
      this.step = 1;
    },
    // 전체 동의 토글
    toggleAgreeAll() {
      const v = this.terms.agreeAll;
      this.terms.tos = v;
      this.terms.privacy = v;
      this.terms.sensitive_content = v;
      this.terms.marketing = v;
    },
    // 개별 체크 변화 시 전체 동의 동기화
    syncAgreeAll() {
      this.terms.agreeAll =
        this.terms.tos &&
        this.terms.privacy &&
        this.terms.sensitive_content &&
        this.terms.marketing;
    },
    async openTermsModal(type) {
      this.modalTitle =
        type === "tos"
          ? "이용약관"
          : type === "privacy"
          ? "개인정보 처리방침"
          : type === "sensitive_content"
          ? "민감정보 처리방침"
          : "마케팅 정보 수신동의";

      try {
        const response = await fetch(`/terms/${type}_2025-10-27.md`);
        if (response.ok) {
          const text = await response.text();
          this.modalContent = this.convertMarkdownToHtml(text);
        } else {
          this.modalContent = "<p>약관 내용을 불러올 수 없습니다.</p>";
        }
      } catch (error) {
        console.error("약관 로드 실패:", error);
        this.modalContent = "<p>약관 내용을 불러올 수 없습니다.</p>";
      }

      this.showTermsModal = true;
    },
    closeTermsModal() {
      this.showTermsModal = false;
      this.modalContent = "";
    },
    convertMarkdownToHtml(markdown) {
      return markdown
        .replace(/^# (.*$)/gim, "<h1>$1</h1>")
        .replace(/^## (.*$)/gim, "<h2>$1</h2>")
        .replace(/^### (.*$)/gim, "<h3>$1</h3>")
        .replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>")
        .replace(/\*(.*?)\*/gim, "<em>$1</em>")
        .replace(/\n\n/gim, "</p><p>")
        .replace(/\n/gim, "<br>")
        .replace(/^(.*)$/gim, "<p>$1</p>");
    },
    async onSignup() {
      if (!this.canSubmitStep2 || this.loading) return;
      this.loading = true;

      try {
        await api("/v1/auth/invitations/accept", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: this.token,
            name: this.form.name,
            email: this.form.email,
            birth_date: this.form.birth,
            gender: this.form.gender,
            password: this.form.password,
            agree_to_terms: this.terms.tos,
            agree_to_privacy: this.terms.privacy,
            agree_to_sensitive_content: this.terms.sensitive_content,
            agree_to_marketing: this.terms.marketing,
          }),
        });

        this.step = 3;
      } catch (e) {
        // do nothing
      } finally {
        this.loading = false;
      }
    },
    goToSignIn() {
      this.$router.push("/signin");
    },
  },
};
</script>

<style scoped>
/* 제목 */
.signup-head {
  margin-bottom: 24px;
  text-align: left;
}

.signup-badge {
  color: #78ff47;
  font-size: 18px;
  line-height: 150%;
  letter-spacing: -0.01em;
}

.signup-title {
  margin: 6px 0 0;
  font-weight: 700;
  font-size: 40px;
  line-height: 100%;
  letter-spacing: -2px;
  text-align: left;
}

/* 폼 */
.signup-form {
  display: grid;
  gap: 16px;
  width: 100%;
}

/* UiInput */
.signup-form :deep(.ui-input) {
  height: 54px;
  border-radius: 5px;
  border: 1px solid #fff !important;
  background: #151718;
  color: #fff;
  padding: 0 14px;
}

.signup-form :deep(.ui-input::placeholder) {
  color: rgba(255, 255, 255, 0.6);
}

.signup-form :deep(.ui-input:focus) {
  outline: none;
  border-color: #fff !important;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
}

.signup-form .ui-btn {
  width: 100%;
}

/* 약관 섹션 */
.terms-section {
  width: 100%;
}

.terms-header {
  margin-bottom: 32px;
  text-align: center;
}

.terms-title {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: #fff;
}

.terms-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
}

.terms-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
}

.terms-item {
  width: 100%;
}

.terms-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
}

.terms-checkbox-input {
  display: none;
}

.terms-checkbox-custom {
  width: 20px;
  height: 20px;
  border: 2px solid #fff;
  border-radius: 4px;
  background: #151718;
  position: relative;
  flex-shrink: 0;
  margin-top: 2px;
  transition: all 0.3s ease;
}

.terms-checkbox-input:checked + .terms-checkbox-custom {
  background: #78ff47;
  border-color: #78ff47;
}

.terms-checkbox-input:checked + .terms-checkbox-custom::after {
  content: "✓";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #000;
  font-weight: bold;
  font-size: 14px;
}

.terms-checkbox-text {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.terms-link {
  color: #78ff47;
  text-decoration: underline;
  text-decoration-color: rgba(120, 255, 71, 0.5);
  transition: all 0.3s ease;
}

.terms-link:hover {
  color: #a8ff71;
  text-decoration-color: #78ff47;
}

.required {
  color: #ff4647;
  font-weight: 500;
}

.optional {
  color: rgba(255, 255, 255, 0.6);
}

.terms-actions {
  display: flex;
  gap: 12px;
  width: 100%;
}

.terms-actions .ui-btn {
  flex: 1;
}

.terms-actions .ui-btn--brand {
  background: var(--brand);
  color: #000;
}

.terms-actions .ui-btn--secondary {
  background: #fff;
  color: #000 !important;
  border: 1px solid #333;
}

/* 성공 화면 */
.success-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
}

.success-icon {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.success-title {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 32px;
  line-height: 100%;
  letter-spacing: -2px;
  color: #ffffff;
  text-align: center;
}

.success-message {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 160%;
  text-align: center;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.8);
}

.success-actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
}

.success-actions .ui-btn {
  width: 100%;
}

@media (max-width: 640px) {
  .signup-title,
  .success-title {
    font-size: 32px;
  }

  .terms-list {
    margin-bottom: 16px;
  }

  .term-item-all .agree-all-text {
    white-space: nowrap;
  }

  .terms-actions {
    flex-direction: row;
    gap: 8px;
  }

  .terms-actions .ui-btn {
    flex: 1;
    min-width: 0;
  }
}

/* 필드 래퍼 */
.field-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-error {
  color: #ff6b6b;
  font-size: 12px;
  margin: 0;
}

/* 비밀번호 확인 래퍼 */
.confirm-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* 성별 선택 */
.ui-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s;
}

.ui-input:focus {
  border-color: #78ff47;
}

.ui-input option {
  background: #1a1a1a;
  color: #fff;
}

/* 약관 동의 */
.agree-all {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: rgba(120, 255, 71, 0.1);
  border: 1px solid rgba(120, 255, 71, 0.3);
  border-radius: 8px;
  margin-bottom: 16px;
  cursor: pointer;
}

.agree-all input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #78ff47;
}

.agree-all span {
  font-weight: 600;
  color: #78ff47;
}

.terms-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 24px;
}

.term-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  cursor: pointer;
}

.terms-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 8px 0;
}

.term-item-all {
  padding: 8px 0;
}

.term-item-all .agree-all-text {
  font-weight: 600;
  font-size: 14px;
  color: #78ff47;
}

.term-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #78ff47;
}

.term-item span {
  flex: 1;
  font-size: 14px;
}

.term-link {
  margin-left: auto;
  background: transparent;
  border: 0;
  color: #78ff47;
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
}

.terms-link {
  color: #78ff47;
  text-decoration: underline;
  text-decoration-color: rgba(120, 255, 71, 0.5);
  transition: all 0.3s ease;
}

.terms-link:hover {
  color: #a8ff71;
  text-decoration-color: #78ff47;
}

.required {
  color: #ff4647;
  font-weight: 500;
}

.optional {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
}

/* 약관 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: #151718;
  border-radius: 20px;
  max-width: 800px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 20px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #fff;
}

.modal-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 32px;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 32px;
}

.terms-content {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  font-size: 14px;
}

.terms-content h1 {
  color: #78ff47;
  font-size: 20px;
  margin: 0 0 16px 0;
  font-weight: 600;
}

.terms-content h2 {
  color: #78ff47;
  font-size: 18px;
  margin: 24px 0 12px 0;
  font-weight: 600;
}

.terms-content h3 {
  color: #fff;
  font-size: 16px;
  margin: 20px 0 8px 0;
  font-weight: 500;
}

.terms-content p {
  margin: 0 0 12px 0;
}

.terms-content strong {
  color: #78ff47;
  font-weight: 600;
}

.terms-content em {
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
}

.modal-footer {
  padding: 24px 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
}

.modal-footer .ui-btn {
  min-width: 120px;
}
</style>
